import { useCallback, useMemo, useState } from "react";

/**
 * Row selection for a paged list, in one of two modes:
 *
 *   ids    — the rows ticked by hand (possibly across pages): `{client_ids: [...]}`
 *   filter — "Select all {total} matching": every row the current filters match,
 *            including rows on pages never loaded, minus the rows unticked since:
 *            `{filters, exclude_ids?}`
 *
 * The second mode is how the spec's worked example acts on 64 clients from a 50-row
 * page. `exclude_ids` is filed as CR-F-4 (the contract's selection is
 * `{client_ids?|filters?}`); it is sent only when a row was unticked.
 *
 * The selection is cleared whenever `resetKey` changes (the filters or the sort), because
 * ids ticked under one filter are not a selection of another.
 */
const fresh = (key) => ({ key, mode: "ids", ids: new Set(), excluded: new Set() });

export default function useSelection(resetKey) {
  const [state, setState] = useState(() => fresh(resetKey));
  // Reset during render when the key changes (React's documented derived-state shape).
  let current = state;
  if (state.key !== resetKey) {
    current = fresh(resetKey);
    setState(current);
  }
  const { mode, ids, excluded } = current;

  const flip = (set, id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  };

  const toggle = useCallback((id) => setState((s) => (s.mode === "filter"
    ? { ...s, excluded: flip(s.excluded, id) }
    : { ...s, ids: flip(s.ids, id) })), []);

  const togglePage = useCallback((pageIds) => setState((s) => {
    if (s.mode === "filter") {
      // The header box of a matching selection clears it: there is no "page" to untick.
      return fresh(s.key);
    }
    const allOn = pageIds.length > 0 && pageIds.every((id) => s.ids.has(id));
    const next = new Set(s.ids);
    pageIds.forEach((id) => (allOn ? next.delete(id) : next.add(id)));
    return { ...s, ids: next };
  }), []);

  const selectAllMatching = useCallback(() => setState((s) => ({ ...s, mode: "filter", ids: new Set(), excluded: new Set() })), []);
  const clear = useCallback(() => setState((s) => fresh(s.key)), []);
  // A row that no longer exists (deleted on its own) leaves both sets: kept as a ticked
  // id it would be counted and sent; kept as an excluded id it would be subtracted from a
  // total that no longer includes it. Either way the count would stop matching the server.
  const forget = useCallback((id) => setState((s) => {
    if (!s.ids.has(id) && !s.excluded.has(id)) return s;
    const ids = new Set(s.ids);
    const excluded = new Set(s.excluded);
    ids.delete(id);
    excluded.delete(id);
    return { ...s, ids, excluded };
  }), []);

  return useMemo(() => ({
    mode,
    isSelected: (id) => (mode === "filter" ? !excluded.has(id) : ids.has(id)),
    isPageSelected: (pageIds) => pageIds.length > 0 && pageIds.every((id) => (mode === "filter" ? !excluded.has(id) : ids.has(id))),
    count: (total) => (mode === "filter" ? Math.max(0, total - excluded.size) : ids.size),
    any: mode === "filter" || ids.size > 0,
    toggle,
    togglePage,
    selectAllMatching,
    clear,
    forget,
    /** The `selection` object for the bulk and export routes. */
    payload: (filters) => {
      if (mode !== "filter") return { client_ids: [...ids] };
      return excluded.size ? { filters, exclude_ids: [...excluded] } : { filters };
    },
  }), [mode, ids, excluded, toggle, togglePage, selectAllMatching, clear, forget]);
}
